import {
  UserType,
  SentEmailCodeValidateFormValues,
} from '@/core/domain/yCarsAuth/User/User.types';
import {
  Alert,
  AutoComplete,
  Col,
  Form,
  Modal,
  ModalProps,
  Row,
  Typography,
} from 'antd';
import FloatingFormItem from '../FloatingFormItem';
import { useIntl } from 'react-intl';
import { useSentEmailValidateCodeUser } from '@/core/domain/yCarsAuth/User/User.hooks';
import { getUserFromLocalStorage } from '@/utils/user';
import { useCustomerContacts } from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';
import { useInsurerContactList } from '@/core/domain/people/InsurerContact/InsurersContact.hooks';
import { ContactType } from '@/core/domain/_shared/ContactType';
import { UserYCars } from '@/types/YCars';

interface Props extends Omit<ModalProps, 'onCancel'> {
  onCancel: VoidFunction;
  user: UserYCars;
}

export default function SentEmailValidateCodeYcarsModal(props: Props) {
  const { matrixId } = getUserFromLocalStorage();
  const user = props.user;

  const intl = useIntl();

  const [form] = Form.useForm<SentEmailCodeValidateFormValues>();

  const sentEmail = useSentEmailValidateCodeUser();

  const customerContacts = useCustomerContacts(
    user.id,
    UserType.customer === user.type && props.open,
  );

  const insurerContacts = useInsurerContactList(
    user.id,
    UserType.insurer === user.type && props.open,
  );

  const customerEmails = customerContacts.data?.flatMap((detail) =>
    detail.detailList
      .filter((contact) => contact.contactType === ContactType.Email)
      .map((contact) => contact.value),
  );

  const insurerEmails = insurerContacts.data?.flatMap((detail) =>
    detail.detailList
      .filter((contact) => contact.contactType === ContactType.Email)
      .map((contact) => contact.value),
  );

  const emailList = customerEmails || insurerEmails;

  function handleCancel() {
    form.resetFields();

    props.onCancel();
  }

  async function handleOk() {
    const values = await form.validateFields();

    await sentEmail.mutateAsync({
      cpfCnpj: user.cpfCnpj,
      email: values.email,
      userId: user.id,
      userType: user.type,
      matrixId: Number(matrixId),
      name: user.name,
      photoUrl: user.photoUrl,
      rentalCompany: user.rentalCompany,
    });

    handleCancel();
  }

  return (
    <Modal
      title="Enviar o email"
      open={props.open}
      onCancel={handleCancel}
      width={500}
      okText="Enviar"
      onOk={handleOk}
      confirmLoading={sentEmail.isLoading}
      destroyOnClose
    >
      <Form form={form}>
        <Row gutter={[12, 12]}>
          <Col span={24}>
            <FloatingFormItem
              name="email"
              label={intl.formatMessage({
                id: 'customers.domain.contact.contactType.email',
              })}
              rules={[
                {
                  type: 'email',
                  message: intl.formatMessage({
                    id: 'commons.form.errors.invalidEmail',
                  }),
                },
              ]}
            >
              <AutoComplete
                showSearch
                optionFilterProp="label"
                options={emailList?.map((email) => ({
                  value: email,
                  label: email,
                }))}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <Alert
              type="warning"
              message={
                <>
                  <Typography.Title level={5}>Informativo</Typography.Title>

                  <Typography.Text>
                    Os e-mails poderão cair na na <strong>caixa de spam</strong>
                    , alertar o cliente.
                  </Typography.Text>
                </>
              }
              style={{ marginBottom: 24 }}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
